import React, { useEffect, useState } from "react";
import { authFetch } from "../api/api";

const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;

const API_URL = `${BASE_URL}/api/hs-codes/tree/`;


const HelpPage = () => {

  const [tree, setTree] = useState([]);

  const [expanded, setExpanded] = useState(
    {}
  );

  const [children, setChildren] = useState(
    {}
  );

  const [loading, setLoading] = useState(
    false
  );

  const [loadingNodes, setLoadingNodes] = useState(
    {}
  );

  const [search, setSearch] = useState("");

  const [searchResults, setSearchResults] = useState(
    []
  );

  const [searchLoading, setSearchLoading] = useState(
    false
  );


  // =====================================================
  // ЗАГРУЗКА КОРНЕВЫХ ГРУПП
  // =====================================================

  const fetchRoot = async () => {

    try {

      setLoading(true);

      const response = await authFetch(API_URL);

      if (!response.ok) {
        throw new Error(
          `Ошибка API: ${response.status}`
        );
      }

      const data = await response.json();

      setTree(
        Array.isArray(data)
          ? data
          : data.results || []
      );

    } catch (error) {

      console.error(
        "Ошибка загрузки ТН ВЭД:",
        error
      );

    } finally {

      setLoading(false);

    }
  };


  // =====================================================
  // ЗАГРУЗКА ДЕТЕЙ
  // =====================================================

  const fetchChildren = async (code) => {

    try {

      setLoadingNodes(
        (prev) => ({
          ...prev,
          [code]: true,
        })
      );

      const response = await authFetch(
        `${API_URL}?parent=${encodeURIComponent(code)}`
      );

      if (!response.ok) {
        throw new Error(
          `Ошибка API: ${response.status}`
        );
      }

      const data = await response.json();

      setChildren(
        (prev) => ({
          ...prev,
          [code]: Array.isArray(data)
            ? data
            : data.results || [],
        })
      );

    } catch (error) {

      console.error(
        `Ошибка загрузки детей ${code}:`,
        error
      );

    } finally {

      setLoadingNodes(
        (prev) => ({
          ...prev,
          [code]: false,
        })
      );

    }
  };


  // =====================================================
  // ОТКРЫТИЕ / ЗАКРЫТИЕ УЗЛА
  // =====================================================

  const toggleNode = async (node) => {

    const code = node.code;

    // Если уже открыт — просто закрываем
    if (expanded[code]) {

      setExpanded(
        (prev) => ({
          ...prev,
          [code]: false,
        })
      );

      return;
    }


    // Если детей ещё нет в памяти —
    // загружаем их
    if (!children[code]) {

      await fetchChildren(code);

    }


    // Открываем
    setExpanded(
      (prev) => ({
        ...prev,
        [code]: true,
      })
    );
  };


  // =====================================================
  // ПЕРВИЧНАЯ ЗАГРУЗКА
  // =====================================================

  useEffect(() => {

    fetchRoot();

  }, []);


  // =====================================================
  // ПОИСК
  // =====================================================

  useEffect(() => {

    const value = search.trim();

    if (!value) {

      setSearchResults([]);

      return;

    }


    const timer = setTimeout(
      async () => {

        try {

          setSearchLoading(true);

          /*
           * Поиск пока делаем через отдельный endpoint.
           *
           * Если его ещё нет — ниже покажу,
           * как его добавить.
           */

          const response = await authFetch(
            `${BASE_URL}/api/hs-codes/search/?q=${encodeURIComponent(value)}`
          );

          if (!response.ok) {

            throw new Error(
              `Ошибка поиска: ${response.status}`
            );

          }

          const data = await response.json();

          setSearchResults(
            Array.isArray(data)
              ? data
              : data.results || []
          );

        } catch (error) {

          console.error(
            "Ошибка поиска ТН ВЭД:",
            error
          );

          setSearchResults([]);

        } finally {

          setSearchLoading(false);

        }

      },
      350
    );


    return () => clearTimeout(timer);

  }, [search]);


  // =====================================================
  // РЕКУРСИВНЫЙ УЗЕЛ
  // =====================================================

  const TreeNode = ({
    node,
    level = 0,
  }) => {

    const isExpanded =
      expanded[node.code];

    const nodeChildren =
      children[node.code] || [];

    const isLoading =
      loadingNodes[node.code];


    return (
      <div>

        {/* ================= NODE ================= */}

        <div
          className="
            flex
            items-center
            gap-3
            py-2
            px-3
            rounded-xl
            hover:bg-gray-50
            transition
            cursor-pointer
            border-b
            border-gray-100
          "
          style={{
            paddingLeft:
              `${16 + level * 28}px`,
          }}
          onClick={() => {

            if (node.has_children) {
              toggleNode(node);
            }

          }}
        >

          {/* ================= ARROW ================= */}

          <div
            className="
              w-5
              h-5
              flex
              items-center
              justify-center
              text-gray-500
              shrink-0
            "
          >

            {node.has_children ? (

              <span
                className="
                  transition-transform
                  duration-200
                "
                style={{
                  transform: isExpanded
                    ? "rotate(90deg)"
                    : "rotate(0deg)",
                }}
              >
                ▶
              </span>

            ) : (

              <span className="text-gray-300">
                •
              </span>

            )}

          </div>


          {/* ================= CODE ================= */}

          <div
            className="
              min-w-[130px]
              font-mono
              font-semibold
              text-gray-900
            "
          >
            {node.code}
          </div>


          {/* ================= TITLE (кликабельный) ================= */}

          <div
            className="
              flex-1
              text-gray-700
              hover:text-blue-600
              hover:underline
              cursor-pointer
            "
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = `/help/${node.code}`;
            }}
          >
            {node.title}
          </div>


          {/* ================= LOADING ================= */}

          {isLoading && (

            <div
              className="
                text-xs
                text-gray-400
              "
            >
              загрузка...
            </div>

          )}

        </div>


        {/* ================= CHILDREN ================= */}

        {isExpanded && (

          <div>

            {nodeChildren.map(
              (child) => (

                <TreeNode
                  key={child.id}
                  node={child}
                  level={level + 1}
                />

              )
            )}

            {!isLoading &&
              nodeChildren.length === 0 && (

                <div
                  className="
                    text-sm
                    text-gray-400
                    py-2
                  "
                  style={{
                    paddingLeft:
                      `${44 + level * 28}px`,
                  }}
                >
                  Нет дочерних кодов
                </div>

            )}

          </div>

        )}

      </div>
    );
  };

  // =====================================================
  // РЕНДЕР
  // =====================================================

  const isSearching =
    search.trim().length > 0;


  return (

    <div
      className="
        min-h-screen
        p-6
        bg-gray-50
      "
    >

      <div
        className="
          max-w-7xl
          mx-auto
        "
      >

        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <div
          className="
            mb-6
          "
        >

          <h1
            className="
              text-2xl
              font-bold
              text-gray-900
              mb-2
            "
          >
            ТН ВЭД
          </h1>

          <p
            className="
              text-gray-500
            "
          >
            Товарная номенклатура внешнеэкономической
            деятельности
          </p>

        </div>


        {/* ================================================= */}
        {/* SEARCH */}
        {/* ================================================= */}

        <div
          className="
            bg-white
            rounded-2xl
            shadow-sm
            border
            border-gray-100
            p-4
            mb-5
          "
        >

          <div
            className="
              relative
            "
          >

            <input
              type="text"
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
              placeholder="
                Поиск по коду или названию товара...
              "
              className="
                w-full
                border
                border-gray-200
                rounded-xl
                px-4
                py-3
                outline-none
                focus:border-gray-400
                focus:ring-2
                focus:ring-gray-100
              "
            />

          </div>

        </div>


        {/* ================================================= */}
        {/* SEARCH RESULTS */}
        {/* ================================================= */}

        {isSearching ? (

          <div
            className="
              bg-white
              rounded-2xl
              shadow-sm
              border
              border-gray-100
              overflow-hidden
            "
          >

            <div
              className="
                px-5
                py-4
                border-b
                font-semibold
              "
            >
              Результаты поиска
            </div>


            {searchLoading ? (

              <div
                className="
                  p-6
                  text-center
                  text-gray-400
                "
              >
                Поиск...
              </div>

            ) : searchResults.length === 0 ? (

              <div
                className="
                  p-6
                  text-center
                  text-gray-400
                "
              >
                Ничего не найдено
              </div>

            ) : (

              <div>

                {searchResults.map(
                  (item) => (

                    <div
                      key={item.id}
                      className="
                        px-5
                        py-3
                        border-b
                        border-gray-100
                        hover:bg-gray-50
                      "
                    >

                      <div
                        className="
                          flex
                          gap-4
                          items-start
                        "
                      >

                        <span
                          className="
                            font-mono
                            font-semibold
                            text-gray-900
                            min-w-[130px]
                          "
                        >
                          {item.code}
                        </span>

                        <span
                          className="
                            text-gray-700
                          "
                        >
                          {item.title}
                        </span>

                      </div>

                    </div>

                  )
                )}

              </div>

            )}

          </div>

        ) : (

          /* ================================================= */
          /* TREE */
          /* ================================================= */

          <div
            className="
              bg-white
              rounded-2xl
              shadow-sm
              border
              border-gray-100
              overflow-hidden
            "
          >

            <div
              className="
                px-5
                py-4
                border-b
                border-gray-100
                font-semibold
                text-gray-900
              "
            >
              Разделы ТН ВЭД
            </div>


            {loading ? (

              <div
                className="
                  p-8
                  text-center
                  text-gray-400
                "
              >
                Загрузка разделов...
              </div>

            ) : tree.length === 0 ? (

              <div
                className="
                  p-8
                  text-center
                  text-gray-400
                "
              >
                Данные отсутствуют
              </div>

            ) : (

              <div>

                {tree.map(
                  (node) => (

                    <TreeNode
                      key={node.id}
                      node={node}
                    />

                  )
                )}

              </div>

            )}

          </div>

        )}

      </div>

    </div>

  );
};


export default HelpPage;